import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const ClinicalNutritionCoursePage: React.FC = () => {
  const clinicalNutritionData = {
    title: "Bachelor of Science in Clinical Nutrition and Dietetics",
    shortName: "B.Sc Clinical Nutrition",
    duration: "3-4 Years",
    description: "A specialized healthcare program focusing on nutrition science, diet therapy, and the clinical application of nutrition for disease prevention and health promotion.",
    overview: "B.Sc Clinical Nutrition and Dietetics is a comprehensive program that combines the science of nutrition with clinical practice. Students learn about human nutrition, food science, metabolism, and the therapeutic use of diet in managing various health conditions. The course covers nutritional assessment, diet planning for different age groups and medical conditions, community nutrition, and food service management. Graduates work as clinical nutritionists, dietitians, and nutrition educators in healthcare settings.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Mathematics as additional subject preferred",
      "Age: 17-25 years",
      "Interest in health and nutrition",
      "Good communication skills"
    ],
    careerProspects: {
      roles: [
        "Clinical Nutritionist",
        "Registered Dietitian",
        "Diet Consultant",
        "Nutrition Educator",
        "Food Safety Officer",
        "Wellness Consultant",
        "Sports Nutritionist",
        "Community Nutritionist",
        "Research Nutritionist",
        "Nutrition Writer"
      ],
      industries: [
        "Hospitals and Clinics",
        "Wellness Centers",
        "Sports Facilities",
        "Food Industry",
        "Research Institutions",
        "Government Health Departments",
        "NGOs and Community Health",
        "Educational Institutions",
        "Pharmaceutical Companies",
        "Private Practice"
      ],
      averageSalary: "₹3-7 LPA (varies by specialization and experience)",
      topRecruiters: [
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Max Healthcare",
        "Manipal Hospitals",
        "AIIMS",
        "Nestle Health Science",
        "Abbott Nutrition",
        "Government Health Services",
        "Sports Academies",
        "Wellness Chains"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Basic Nutrition",
          "Food Science",
          "Biochemistry",
          "Anatomy and Physiology",
          "Food Chemistry",
          "Mathematics",
          "English Communication",
          "Computer Applications"
        ]
      },
      {
        year: 2,
        subjects: [
          "Clinical Nutrition",
          "Therapeutic Nutrition",
          "Community Nutrition",
          "Food Microbiology",
          "Nutritional Biochemistry",
          "Pathophysiology",
          "Food Service Management",
          "Research Methodology"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Clinical Nutrition",
          "Pediatric Nutrition",
          "Geriatric Nutrition",
          "Sports Nutrition",
          "Public Health Nutrition",
          "Nutrition Counseling",
          "Food Quality and Safety",
          "Internship/Clinical Training"
        ]
      },
      {
        year: 4,
        subjects: [
          "Nutrition in Disease Management",
          "Nutrition Research",
          "Nutrigenomics",
          "Food Policy and Regulations",
          "Entrepreneurship in Nutrition",
          "Advanced Internship",
          "Project Work",
          "Professional Ethics"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc Clinical Nutrition",
      "Merit-based selection or entrance examination",
      "Counseling and seat allocation",
      "Document verification",
      "Medical fitness certificate",
      "Fee payment and admission confirmation",
      "Orientation program",
      "Begin academic curriculum"
    ],
    topColleges: [
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹10,000 PA",
        ranking: "Premier Government Institute"
      },
      {
        name: "Lady Irwin College",
        location: "New Delhi",
        fees: "₹25,000 PA",
        ranking: "Top Women's College"
      },
      {
        name: "JSS Academy of Higher Education",
        location: "Mysore, Karnataka",
        fees: "₹1.5 LPA",
        ranking: "Deemed University"
      },
      {
        name: "Osmania University",
        location: "Hyderabad, Telangana",
        fees: "₹15,000 PA",
        ranking: "State University"
      },
      {
        name: "Tamil Nadu Agricultural University",
        location: "Coimbatore, Tamil Nadu",
        fees: "₹20,000 PA",
        ranking: "Agricultural University"
      }
    ],
    skills: [
      "Nutritional Assessment",
      "Diet Planning",
      "Counseling Skills",
      "Clinical Knowledge",
      "Food Analysis",
      "Research Skills",
      "Communication Skills",
      "Computer Proficiency",
      "Problem Solving",
      "Empathy",
      "Time Management",
      "Health Promotion"
    ],
    futureScope: "Clinical nutrition is experiencing tremendous growth due to increasing awareness of diet-related diseases, aging population, and lifestyle changes. Future opportunities include precision nutrition, nutrigenomics, digital health coaching, and integration with telemedicine platforms. Specializations in areas like oncology nutrition, pediatric nutrition, and sports nutrition are in high demand. The field is expanding into wellness tourism, corporate wellness programs, and personalized nutrition services.",
    faqs: [
      {
        question: "What is the difference between a Nutritionist and a Dietitian?",
        answer: "Dietitians have formal education and professional registration, while nutritionists may have varied educational backgrounds. Dietitians can work in clinical settings and provide medical nutrition therapy, while nutritionists typically focus on general wellness and education."
      },
      {
        question: "Can I start my own nutrition consultancy?",
        answer: "Yes, after completing the degree and gaining experience, you can start your own nutrition consultancy. Many nutritionists run successful private practices, online consultations, or wellness centers."
      },
      {
        question: "Is there scope for higher education after B.Sc Clinical Nutrition?",
        answer: "Yes, you can pursue M.Sc Clinical Nutrition, MBA in Food and Nutrition, PhD in Nutrition Science, or specialized diplomas in sports nutrition, clinical nutrition, or public health nutrition."
      },
      {
        question: "What are the job prospects in government sector?",
        answer: "Government opportunities include positions as Food Safety Officers, Nutrition Educators in ICDS programs, Public Health Nutritionists, and researchers in institutions like ICMR and NIN."
      },
      {
        question: "How important is clinical exposure during the course?",
        answer: "Clinical exposure is crucial as it provides hands-on experience in patient care, diet planning for medical conditions, and understanding the practical application of nutrition therapy in healthcare settings."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {clinicalNutritionData.title} ({clinicalNutritionData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={clinicalNutritionData} />
      </div>
    </div>
  );
};

export default ClinicalNutritionCoursePage;